import { CardString, Hand, Suit, Value } from 'pokersolver'

/**
 * Draw analysis utilities for poker hands
 * Analyzes potential draws (flush draws, straight draws) and calculates odds
 */

import type { GameState, EnginePlayer } from '@ppwr/poker-engine'

interface StraightDrawResult {
  outs: number
  lowCard: number
  highCard?: number
  missingCard?: number
}

export const DRAW_NAMES = {
  flush: 'Flush Draw',
  openEndedStraight: 'Open-Ended Straight Draw',
  gutshotStraight: 'Gutshot Straight Draw'
} as const

export type DrawName = (typeof DRAW_NAMES)[keyof typeof DRAW_NAMES]

function hasOpenEndedStraightDraw(ranks: number[]): StraightDrawResult | null {
  const uniqueRanks: number[] = [...new Set(ranks)].sort((a, b) => a - b)
  for (let i = 0; i < uniqueRanks.length - 3; i++) {
    const window: number[] = uniqueRanks.slice(i, i + 4)
    const min: number = Math.min(...window)
    const max: number = Math.max(...window)
    if (max - min <= 4 && window.length === 4) {
      const lowCard: number = min - 1
      const highCard: number = max + 1
      const outs: number = [lowCard, highCard].filter((card: number) => card >= 2 && card <= 14).length * 4
      return { outs, lowCard, highCard }
    }
  }
  return null
}

function hasGutshotStraightDraw(ranks: number[]): StraightDrawResult | null {
  const uniqueRanks: number[] = [...new Set(ranks)].sort((a, b) => a - b)
  for (let i = 0; i < uniqueRanks.length - 3; i++) {
    const window: number[] = uniqueRanks.slice(i, i + 4)
    const min: number = Math.min(...window)
    const max: number = Math.max(...window)
    if (max - min === 4 && window.length === 4) {
      for (let k = min; k <= max; k++) {
        if (!window.includes(k)) {
          const missingCard: number = k
          return { outs: 4, missingCard, lowCard: missingCard }
        }
      }
    }
  }
  return null
}

// Define interfaces for draw analysis
export interface DrawAnalysis {
  currentHand: string
  isStrong: boolean
  draws: DrawName[]
}

/**
 * Get playing cards (player cards + community cards)
 */
function getPlayingCards(player: EnginePlayer, gameState: GameState): CardString[] {
  return [...player.cards, ...gameState.communityCards] as CardString[]
}

/**
 * Analyze draws for a player
 * Detects flush draws, open-ended straight draws, and gutshot straight draws
 * Calculates odds for completing each draw
 * @param gameState - Current game state
 * @param player - Player to analyze
 * @returns Draw analysis with detected draws and odds
 */
export function analyzeDraws(gameState: GameState, player: EnginePlayer): DrawAnalysis {
  const cards = getPlayingCards(player, gameState)
  const solvedHand = Hand.solve(cards)

  // No cards left to come: a "draw" cannot improve, so only the made hand
  // exists. On the river (or later), describing a possible straight/flush
  // draw is nonsense — the hand is what it is. Strength still comes from the
  // solved hand below.
  const noCardsToCome =
    gameState.boardState === 'river' || gameState.boardState === 'showdown' || gameState.boardState === 'hand_complete'

  const result: DrawAnalysis = {
    currentHand: solvedHand.descr, // e.g., "Ace High", "Pair of Kings"
    isStrong: false,
    draws: []
  }

  // Check if already has a strong hand
  if (
    solvedHand.descr.includes('Flush') ||
    solvedHand.descr.includes('Straight') ||
    solvedHand.descr.includes('Full House') ||
    solvedHand.descr.includes('Three of a Kind') ||
    solvedHand.descr.includes('Four of a Kind') ||
    solvedHand.descr.includes('Straight Flush') ||
    solvedHand.descr.includes('Royal Flush')
  ) {
    result.isStrong = true
    return result
  }

  // Nothing to draw to — return the made-hand analysis only.
  if (noCardsToCome) {
    return result
  }

  // Get card pool for draw detection
  const cardPool = solvedHand.cardPool.map((card) => ({
    rank: card.rank as number, // Numeric rank (2-14, Ace=14)
    value: card.value as Value,
    suit: card.suit as Suit // 'h', 's', 'd', 'c'
  }))

  // Check for Flush Draw (4 cards of the same suit)
  const suitCounts = cardPool.reduce(
    (acc, card) => {
      acc[card.suit] = (acc[card.suit] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  const flushSuit = Object.keys(suitCounts).find((suit) => suitCounts[suit] === 4)
  if (flushSuit) {
    result.draws.push(DRAW_NAMES.flush)
  }

  // Check for Straight Draws
  const ranks = cardPool.map((card) => card.rank)

  // Open-ended straight draw
  const openEnded: StraightDrawResult | null = hasOpenEndedStraightDraw(ranks)
  if (openEnded) {
    result.draws.push(DRAW_NAMES.openEndedStraight)
  }

  // Gutshot straight draw
  const gutshot: StraightDrawResult | null = hasGutshotStraightDraw(ranks)
  if (gutshot) {
    result.draws.push(DRAW_NAMES.gutshotStraight)
  }

  return result
}
